import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom';

import UserRole from '@careerstart/wae-common/src/main/constants/user-role';
import LanguageConverter from '@careerstart/wae-common/src/main/helperFunction/LanguageConverter';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { Card, CardContent, FormControlLabel, RadioGroup } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Radio from '@mui/material/Radio';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';

import CancelJobDrawer from '../../../main-deprecated/views/jobs/jobList/detail/SubComponents/CancelJobDrawer';
import NumberIncrementDecrement from '../../components/NumberIncrementDecrement';
import selectUser from '../../store/selectors/appSelector';
import theme from '../../theme/theme';
import {
  epochToDateInReadableFormat,
  epochToTimeInReadableFormat,
  isValidDateInstance,
} from '../../utils';
import { ROUTE_CONSTANTS } from '../app/RouteBuilding/routeConstants';

import OverstaffOpenings from './components/OverstaffOpenings';
import JOB_TYPE from './constants';
import {
  cancelSeriesJobV2,
  cancelSingleJobV2,
  updateExtensionGroupV2,
  updateGroupOpeningsV2,
  updateShiftOpeningsV2,
} from './jobOrderDetailReducer';
import {
  selectIsCancelProcessingV2,
  selectIsCancelSeriesProcessingV2,
  selectJobOrderDetailV2,
} from './jobOrderDetailSelectors';
import { createJobOrderDetailLeftSharedStyles } from './jobOrderDetailStyles';

const generateDateTextColor = (isInRange, isCurrentMonth, isSelected) => {
  if (isInRange) {
    if (isSelected) {
      return `${theme?.components?.detailLeft?.palette?.backgroundColor}`;
    }
    return 'primary.main';
  }
  return isCurrentMonth ? 'text.primary' : 'text.secondary';
};
const { titleStyle, boldText, subTitleStyle, subText, buttonStyle } =
  createJobOrderDetailLeftSharedStyles(theme);

const headerStyle = {
  fontFamily: theme.components.detailLeft.header.typography.fontFamily,
  fontSize: theme.components.detailLeft.header.typography.fontSize,
};

const MAX_OPENINGS = 500;
const MIN_OPENINGS = 0;
const isInvalidInputValue = (value) =>
  value === null || value === undefined || value === '' || value < 0 || value > MAX_OPENINGS;

const JobOrderDetailLeft = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isCancelProcessing = useSelector(selectIsCancelProcessingV2);
  const isCancelSeriesProcessing = useSelector(selectIsCancelSeriesProcessingV2);
  const user = useSelector(selectUser);
  const jobOrderDetail = useSelector(selectJobOrderDetailV2);
  const { id } = useParams();

  const [isCancelJobDrawerOpen, setIsCancelJobDrawerOpen] = useState(false);
  const [cancelJobType, setCancelJobType] = useState(null);

  const isExtensionGroupExist = !!jobOrderDetail?.extensionGroup;
  const corporationName = jobOrderDetail?.corpName;
  const extensionGroupId = jobOrderDetail?.extensionGroup?.id;
  const extensionGroupNumOpenings = jobOrderDetail?.extensionGroup?.numOpenings;
  const extensionGroupOverstaff = jobOrderDetail?.extensionGroup?.numOverstaff || 0;
  const numOpenings = jobOrderDetail?.numOpenings;
  const numOverstaff = jobOrderDetail?.numOverstaff;
  const extensionStart = jobOrderDetail?.extensionGroup?.start;
  const extensionEnd = jobOrderDetail?.extensionGroup?.end;
  const scheduledJobData = jobOrderDetail?.extensionGroup?.scheduledOrders;
  const extensionCadence = jobOrderDetail?.extensionGroup?.cadence;
  const creatingUserName = jobOrderDetail?.creatingUser?.name;

  const capitalizedDays = (extensionCadence || []).map((day) =>
    typeof day === 'string' && day.length > 0
      ? day.charAt(0).toUpperCase() + day.slice(1).toLowerCase()
      : ''
  );
  const [endDateInput, setEndDateInput] = useState(null); // Date | null
  const [newSingleOpeningsValue, setNewSingleOpeningsValue] = useState(numOpenings || 0);
  const [newGroupOpeningsValue, setNewGroupOpeningsValue] = useState(
    extensionGroupNumOpenings || 0
  );
  const [openShift, setOpenShift] = useState(JOB_TYPE.THIS_SHIFT);
  const [dailyValue, setDailyValue] = useState(
    typeof jobOrderDetail?.start === 'number' ? new Date(jobOrderDetail.start) : null
  );

  const handelCancelJob = (jobInfo) => {
    if (cancelJobType === JOB_TYPE.THIS_SHIFT) {
      dispatch(cancelSingleJobV2(jobInfo));
    }
    // extensionGroup job order
    else dispatch(cancelSeriesJobV2({ extensionGroup: extensionGroupId }));
  };

  useEffect(() => {
    if (extensionEnd) {
      setEndDateInput(new Date(extensionEnd));
    } else {
      setEndDateInput(null);
    }
  }, [extensionEnd]);

  useEffect(() => {
    if (typeof jobOrderDetail?.start === 'number' && !Number.isNaN(jobOrderDetail.start)) {
      setDailyValue(new Date(jobOrderDetail.start));
    }
  }, [jobOrderDetail?.start]);

  // Sync state when job order data loads
  useEffect(() => {
    if (numOpenings !== undefined && numOpenings !== null) {
      setNewSingleOpeningsValue(numOpenings);
    }
  }, [numOpenings]);

  useEffect(() => {
    if (extensionGroupNumOpenings !== undefined && extensionGroupNumOpenings !== null) {
      setNewGroupOpeningsValue(extensionGroupNumOpenings);
    }
  }, [extensionGroupNumOpenings]);

  const isSameDay = (epochTime1, epochTime2) => {
    const date1 = new Date(epochTime1);
    const date2 = new Date(epochTime2);

    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };

  const handleCancelSingleJob = () => {
    setCancelJobType(JOB_TYPE.THIS_SHIFT);
    setIsCancelJobDrawerOpen(true);
  };

  const handleCancelSeriesJob = () => {
    setCancelJobType(JOB_TYPE.ENTIRE_SERIES);
    setIsCancelJobDrawerOpen(true);
  };

  const shouldDisableBeforeStartDate = (date) =>
    moment(date).isBefore(jobOrderDetail?.extensionGroup?.start);

  const shouldDisableAfterEndDate = (date) => {
    const boundary =
      jobOrderDetail?.extensionGroup?.end ||
      (isValidDateInstance(endDateInput) ? endDateInput : null);
    return boundary ? moment(date).isAfter(boundary) : false;
  };

  const renderDay = (date, selectedDates) => {
    const currentDate = moment(date);
    const isInRange = scheduledJobData?.find((job) => isSameDay(job.date, currentDate.valueOf()));

    const isCurrentMonth = moment().isSame(currentDate, 'month');
    const safeSelectedDates = Array.isArray(selectedDates) ? selectedDates : [];
    const isSelected = safeSelectedDates.some((selectedDate) =>
      moment(date).isSame(selectedDate, 'day')
    );
    return (
      <Box
        key={currentDate.valueOf()}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: '50%',
        }}
      >
        <Button
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: generateDateTextColor(isInRange, isCurrentMonth, isSelected),
            zIndex: 1,
            borderRadius: '50%',
            backgroundColor: isSelected ? 'primary.main' : 'transparent',
            width: 39,
            height: 39,
            minWidth: 36,
            ':hover': {
              backgroundColor: theme?.components?.detailLeft?.palette?.buttonColor,
            },
          }}
          onClick={() => {
            const jobInfo = scheduledJobData?.find((job) =>
              isSameDay(job.date, currentDate.valueOf())
            );
            if (jobInfo) {
              const jobIdPath = generatePath(`/${ROUTE_CONSTANTS.JOB_ORDERS}/:id`, {
                id: jobInfo.id,
              });
              navigate(`${jobIdPath}${location.search}`);
            }
            setDailyValue(new Date(currentDate.valueOf()));
          }}
          disabled={!isInRange}
        >
          <Typography variant="body2" sx={{ textAlign: 'center' }}>
            {currentDate.date()}
          </Typography>
          {isInRange && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 4,
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: 'primary.main',
              }}
            />
          )}
        </Button>
      </Box>
    );
  };

  const handleOpenShiftChange = useCallback((event) => {
    setOpenShift(event.target.value);
  }, []);

  const handleUpdateEndDate = () => {
    if (isValidDateInstance(endDateInput)) {
      dispatch(
        updateExtensionGroupV2({
          extensionGroup: extensionGroupId,
          fields: { end: endDateInput.getTime() },
        })
      );
    }
  };

  const handleNumOpeningsSubmit = () => {
    if (openShift === JOB_TYPE.THIS_SHIFT) {
      dispatch(
        updateShiftOpeningsV2({
          jobOrderId: id,
          numOpenings: parseInt(newSingleOpeningsValue, 10),
        })
      );
    } else {
      dispatch(
        updateGroupOpeningsV2({
          extensionGroupId,
          numOpenings: parseInt(newGroupOpeningsValue, 10),
        })
      );
    }
  };

  const createdByLine = [
    LanguageConverter('jobDetails.createdBy', creatingUserName || ''),
    LanguageConverter(
      'jobDetails.createdOn',
      epochToDateInReadableFormat(jobOrderDetail?.creatingUser?.createdAt)
    ),
    LanguageConverter(
      'jobDetails.createdAt',
      epochToTimeInReadableFormat(jobOrderDetail?.creatingUser?.createdAt)
    ),
  ].join(' ');

  return (
    <Box sx={{ maxWidth: 300 }}>
      <Typography sx={headerStyle}>{jobOrderDetail?.name}</Typography>
      <Card sx={{ maxWidth: '100%', width: '100%' }}>
        <CardContent sx={{ backgroundColor: theme?.components?.detailLeft?.palette?.greyBg }}>
          <Typography sx={titleStyle}>{LanguageConverter('jobDetails.title')}</Typography>
          <Typography sx={subTitleStyle}>{LanguageConverter('jobDetails.corporation')}</Typography>
          <Typography sx={boldText}>{corporationName}</Typography>
          <Typography sx={subTitleStyle}>{LanguageConverter('jobDetails.shiftTime')}</Typography>
          <Typography sx={boldText}>
            {epochToTimeInReadableFormat(jobOrderDetail?.start)} --{' '}
            {epochToTimeInReadableFormat(jobOrderDetail?.end)}
          </Typography>
          <Typography sx={titleStyle}>{LanguageConverter('jobDetails.location')}</Typography>
          <Typography sx={boldText}>
            {jobOrderDetail?.location?.address}, {jobOrderDetail?.location?.city}
          </Typography>
          {creatingUserName && (
            <Typography
              data-testid="job-order-creating-user"
              sx={{ ...boldText, marginTop: '8px' }}
            >
              {createdByLine}
            </Typography>
          )}
        </CardContent>
      </Card>
      <Box>
        {isExtensionGroupExist ? (
          <>
            <Box>
              <Typography sx={titleStyle}>
                {LanguageConverter('jobDetails.adjustEndDate')}
              </Typography>
              <Typography sx={{ ...subTitleStyle, marginBottom: 1 }}>
                {LanguageConverter('jobDetails.adjustSubtitle')}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <table border="0" cellSpacing="0" cellPadding="0">
                  <tbody>
                    <tr>
                      <td>
                        <DatePicker
                          label={LanguageConverter('jobDetails.startDate')}
                          value={
                            typeof extensionStart === 'number' ? new Date(extensionStart) : null
                          }
                          readOnly
                          renderInput={(params) => <TextField {...params} />}
                        />
                      </td>
                      <td aria-hidden="true">&nbsp;</td>
                      <td>
                        <DatePicker
                          label={LanguageConverter('jobDetails.endDate')}
                          value={
                            extensionEnd ? endDateInput ?? new Date(extensionEnd) : endDateInput
                          }
                          shouldDisableDate={(date) => {
                            if (extensionEnd) {
                              return (
                                shouldDisableAfterEndDate(date) ||
                                shouldDisableBeforeStartDate(date)
                              );
                            }
                            return shouldDisableBeforeStartDate(date);
                          }}
                          onChange={(newValue) => {
                            setEndDateInput(newValue || null);
                          }}
                          format="DD-MM-YYYY"
                          renderInput={(params) => <TextField {...params} />}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={3}>
                        <Button
                          onClick={handleUpdateEndDate}
                          variant="outlined"
                          sx={buttonStyle}
                          disabled={!isValidDateInstance(endDateInput)}
                        >
                          {LanguageConverter('jobDetails.updateEndDate')}
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </LocalizationProvider>
            </Box>
            <Box>
              <Typography sx={titleStyle}> {LanguageConverter('jobDetails.dailyView')}</Typography>
              <Typography sx={subTitleStyle}>
                {LanguageConverter('jobDetails.viewManage')}
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box sx={{ position: 'relative', right: 20 }}>
                  <StaticDatePicker
                    displayStaticWrapperAs="desktop"
                    value={dailyValue}
                    renderDay={renderDay}
                    onChange={(newValue) => {
                      setDailyValue(newValue);
                    }}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </Box>
              </LocalizationProvider>
            </Box>
          </>
        ) : null}
        <Box>
          <Typography sx={{ ...titleStyle, marginTop: 0 }}>
            {LanguageConverter('jobDetails.changeOpenings')}
          </Typography>
          <Typography sx={subTitleStyle}>
            {isExtensionGroupExist
              ? LanguageConverter('jobDetails.changeOptions.openings')
              : LanguageConverter('jobDetails.changeShift')}
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              aria-label="options"
              name="options"
              value={openShift}
              onChange={handleOpenShiftChange}
              orientation="vertical"
            >
              <FormControlLabel
                value={JOB_TYPE.THIS_SHIFT}
                control={<Radio />}
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      padding: 1,
                      borderColor: 'grey.300',
                      borderRadius: 1,
                      color: openShift === JOB_TYPE.THIS_SHIFT ? 'text.primary' : 'text.secondary',
                    }}
                  >
                    <Box sx={boldText}>{LanguageConverter('jobDetails.thisShift')}</Box>
                    <Box>{epochToDateInReadableFormat(jobOrderDetail?.start)}</Box>
                    {openShift === JOB_TYPE.THIS_SHIFT && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 1,
                          mt: 2,
                        }}
                      >
                        <Typography sx={{ ...subText, color: 'primary.main', fontWeight: 'bold' }}>
                          {LanguageConverter('jobDetails.openings')}
                        </Typography>
                        <NumberIncrementDecrement
                          value={newSingleOpeningsValue}
                          onChange={setNewSingleOpeningsValue}
                          min={MIN_OPENINGS}
                          max={MAX_OPENINGS}
                          testId="newShiftOpenings"
                        />
                      </Box>
                    )}
                  </Box>
                }
              />
              {isExtensionGroupExist ? (
                <FormControlLabel
                  value={JOB_TYPE.ENTIRE_SERIES}
                  control={<Radio />}
                  label={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        padding: 1,
                        borderColor: 'grey.300',
                        borderRadius: 1,
                        color:
                          openShift === JOB_TYPE.ENTIRE_SERIES ? 'text.primary' : 'text.secondary',
                      }}
                      data-testid="groupOpeningsRadio"
                    >
                      <Box sx={boldText}>{LanguageConverter('jobDetails.series')}</Box>
                      <Box sx={subTitleStyle}>
                        {epochToDateInReadableFormat(extensionStart)}-
                        {extensionEnd
                          ? epochToDateInReadableFormat(extensionEnd)
                          : LanguageConverter('jobDetails.ongoing')}
                      </Box>
                      <Box sx={subText}>{capitalizedDays?.join(', ')}</Box>
                      {openShift !== JOB_TYPE.THIS_SHIFT && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1,
                            mt: 2,
                          }}
                        >
                          <Typography
                            sx={{ ...subText, color: 'primary.main', fontWeight: 'bold' }}
                          >
                            {LanguageConverter('jobDetails.openings')}
                          </Typography>
                          <NumberIncrementDecrement
                            value={newGroupOpeningsValue}
                            onChange={setNewGroupOpeningsValue}
                            min={MIN_OPENINGS}
                            max={MAX_OPENINGS}
                            testId="newGroupOpenings"
                          />
                        </Box>
                      )}
                    </Box>
                  }
                />
              ) : null}
            </RadioGroup>
          </FormControl>
          <Box>
            <Button
              disabled={isInvalidInputValue(
                openShift === JOB_TYPE.THIS_SHIFT ? newSingleOpeningsValue : newGroupOpeningsValue
              )}
              onClick={handleNumOpeningsSubmit}
              variant="outlined"
              sx={buttonStyle}
              data-testid="openingsUpdateSubmit"
            >
              {LanguageConverter('button.openings.update')}
            </Button>
          </Box>
        </Box>
        {(user?.role === UserRole.ADMIN || user?.role === UserRole.RECRUITER) && (
          <OverstaffOpenings
            jobStart={jobOrderDetail?.start}
            extGroupStart={jobOrderDetail?.extensionGroup?.start}
            extGroupEnd={jobOrderDetail?.extensionGroup?.end}
            capitalizedDays={capitalizedDays}
            overstaffInitialValue={numOverstaff}
            extGroupOverstaffInitialValue={extensionGroupOverstaff}
          />
        )}

        <Box>
          <Typography sx={titleStyle}>{LanguageConverter('jobDetails.cancelShift')}</Typography>
          <Box>
            <Button
              disabled={isCancelProcessing}
              variant="outlined"
              onClick={handleCancelSingleJob}
              sx={buttonStyle}
            >
              {LanguageConverter('jobDetails.thisShift')}
            </Button>
            <br />
            {isExtensionGroupExist ? (
              <Button
                disabled={isCancelSeriesProcessing}
                variant="outlined"
                onClick={handleCancelSeriesJob}
                sx={buttonStyle}
              >
                {LanguageConverter('jobDetails.allShifts')}
              </Button>
            ) : null}
          </Box>
        </Box>
      </Box>
      <CancelJobDrawer
        jobId={id}
        numOpenings={numOpenings}
        start={cancelJobType === JOB_TYPE.THIS_SHIFT ? jobOrderDetail?.start : extensionStart}
        jobType={cancelJobType}
        user={user}
        isOpen={isCancelJobDrawerOpen}
        onClose={() => setIsCancelJobDrawerOpen(false)}
        handleCancelJob={handelCancelJob}
      />
    </Box>
  );
};

export default JobOrderDetailLeft;
